import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, ArrowUpRight, Ticket } from 'lucide-react';
import useLang from '../../hooks/useLang';
import { getImageUrl } from '../../utils/imageUrl';
import { eventTicketUrl } from '../../utils/upcomingEvents';

const EventPoster = ({ event }) => {
  const [failed, setFailed] = useState(false);
  return event.poster_url && !failed ? (
    <img src={getImageUrl(event.poster_url)} alt={event.title} onError={() => setFailed(true)}
      loading="lazy" className="max-h-[560px] w-full object-contain" />
  ) : (
    <div className="flex min-h-64 items-center justify-center p-12" aria-hidden="true">
      <img src="/images/logo.png" alt="" className="w-64 max-w-full" />
    </div>
  );
};

const TicketsSection = ({ events = [], status = 'loading', retry }) => {
  const { t } = useTranslation();
  const lang = useLang();
  const event = events[0];
  const ticketUrl = eventTicketUrl(event);
  const buttonClass = 'inline-flex items-center justify-center gap-3 rounded-full bg-[#C9A84C] px-7 py-4 font-display uppercase tracking-wider text-[#060606] hover:bg-[#E2C56A] transition-colors';
  const date = event ? new Intl.DateTimeFormat(lang, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Brussels',
  }).format(new Date(`${event.date}T12:00:00Z`)) : '';

  return (
    <section id="tickets" className="scroll-mt-24 bg-[#060606] px-5 py-16 md:py-24" aria-labelledby="next-event-title" data-testid="tickets-section">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#C9A84C]">Invasion Latina · Brussels</p>
            <h2 id="next-event-title" className="font-display text-4xl uppercase text-[#F5F0E8] md:text-6xl">{t('next_event.title')}</h2>
          </div>
          <Link to="/agenda" className="inline-flex items-center gap-2 text-sm text-[#C9A84C] hover:underline">
            {t('next_event.agenda')} <ArrowUpRight size={18} />
          </Link>
        </div>
        {status === 'loading' ? (
          <div role="status" className="rounded-3xl border border-white/10 p-12 text-white/60">{t('next_event.loading')}</div>
        ) : status === 'error' ? (
          <div role="status" className="rounded-3xl border border-white/10 p-8 text-[#F5F0E8]">
            <p className="mb-5">{t('next_event.error')}</p>
            <button type="button" onClick={retry} className={buttonClass}>{t('next_event.retry')}</button>
          </div>
        ) : event ? (
          <article className="overflow-hidden rounded-3xl border border-[#C9A84C]/25 bg-white/[0.03] md:grid md:grid-cols-2" data-testid="next-event-card">
            <div className="flex items-center justify-center bg-black/40">
              <EventPoster key={`${event.id}-${event.poster_url}`} event={event} />
            </div>
            <div className="flex flex-col justify-center p-7 md:p-12">
              <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#C9A84C]">{t(event.type === 'openair' ? 'next_event.openair' : 'next_event.monthly')}</p>
              <h3 className="font-display text-3xl uppercase text-[#F5F0E8] md:text-5xl">{event.title}</h3>
              {event.subtitle && <p className="mt-3 text-lg text-white/65">{event.subtitle}</p>}
              <div className="my-8 space-y-5 text-[#F5F0E8]">
                <div className="flex gap-3"><CalendarDays className="mt-1 shrink-0 text-[#C9A84C]" size={20} /><div>
                  <time dateTime={event.date} className="capitalize">{date}</time>
                  {event.time && <p className="mt-1 text-sm text-white/55">{event.time}</p>}
                </div></div>
                <div className="flex gap-3"><MapPin className="mt-1 shrink-0 text-[#C9A84C]" size={20} /><div>
                  <p>{event.venue}</p><p className="mt-1 text-sm text-white/55">{[event.address, event.city].filter(Boolean).join(' · ')}</p>
                </div></div>
              </div>
              {ticketUrl ? <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className={buttonClass} data-testid="next-event-tickets">
                <Ticket size={20} />{t('next_event.book')}<ArrowUpRight size={18} />
              </a> : <p className="rounded-xl border border-[#C9A84C]/25 px-5 py-4 text-center text-[#C9A84C]">
                {t(event.status === 'sold_out' ? 'next_event.sold_out' : 'next_event.soon')}
              </p>}
            </div>
          </article>
        ) : (
          <div className="rounded-3xl border border-[#C9A84C]/25 bg-gradient-to-br from-[#C9A84C]/10 to-transparent px-7 py-14 text-center md:py-20" data-testid="next-event-empty">
            <CalendarDays size={36} className="mx-auto mb-6 text-[#C9A84C]" aria-hidden="true" />
            <h3 className="font-display text-3xl uppercase text-[#F5F0E8] md:text-4xl">{t('next_event.empty_title')}</h3>
            <p className="mx-auto mt-4 max-w-lg leading-relaxed text-white/60">{t('next_event.empty_text')}</p>
            <a href="https://www.instagram.com/invasionlatina/" target="_blank" rel="noopener noreferrer" className={`${buttonClass} mt-8`}>
              {t('next_event.follow')}<ArrowUpRight size={18} />
            </a>
          </div>
        )}
      </div>
    </section>
  );

};

export default TicketsSection;
